/**
 * Shared device status calculation hook
 * Consolidates device online/offline status logic and time formatting
 * Used by both TopNavigation (DeviceStatusIndicators) and DeviceInfo components
 * 
 * Features:
 * - Real-time connection status (online/offline based on 60s threshold)
 * - Periodic status updates (every 10 seconds)
 * - Time difference formatting for "last seen" display
 * - Memoized calculations for optimal performance
 */

import { useEffect, useMemo, useState } from 'react'

// Shared constants
export const DEVICE_STATUS_CONSTANTS = Object.freeze({
  ONLINE_THRESHOLD_MS: 60_000, // 60 seconds
  STATUS_UPDATE_INTERVAL_MS: 10_000, // Update every 10 seconds
})

// Shared configuration objects
export const CONNECTION_STATUS_CONFIGS = Object.freeze({
  online: { 
    icon: 'bi-wifi', 
    color: 'text-success', 
    text: 'Online', 
    dot: 'bg-success', 
  },
  offline: { 
    icon: 'bi-wifi-off', 
    color: 'text-danger', 
    text: 'Offline', 
    dot: 'bg-danger', 
  },
  connecting: { 
    icon: 'bi-wifi-1', 
    color: 'text-warning', 
    text: 'Connecting', 
    dot: 'bg-warning', 
  },
  unknown: { 
    icon: 'bi-question-circle', 
    color: 'text-muted', 
    text: 'Unknown', 
    dot: 'bg-secondary', 
  },
})

export const HEALTH_STATUS_CONFIGS = Object.freeze({
  healthy: { 
    icon: 'bi-check-circle-fill', 
    color: 'text-success', 
    text: 'Healthy', 
  },
  warning: { 
    icon: 'bi-exclamation-triangle-fill', 
    color: 'text-warning', 
    text: 'Warning', 
  },
  critical: { 
    icon: 'bi-x-circle-fill', 
    color: 'text-danger', 
    text: 'Critical', 
  },
  unknown: { 
    icon: 'bi-question-circle', 
    color: 'text-muted', 
    text: 'Unknown', 
  },
})

/**
 * Format time difference for display
 * @param {number} diffMs - Time difference in milliseconds
 * @returns {string} Formatted time string (e.g., "5s ago", "2m ago", "1h ago", "3d ago")
 */
export const formatTimeDifference = (diffMs) => {
  const seconds = Math.floor(diffMs / 1000)
  if (seconds < 60) return `${seconds}s ago`
  
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  
  const days = Math.floor(hours / 24)
  return `${days}d ago`
}

/**
 * Calculate device connection status based on last seen timestamp
 * @param {Object} status - Device status object with lastSeen timestamp
 * @returns {string} Connection status: 'online', 'offline', or 'unknown'
 */
export const calculateConnectionStatus = (status) => {
  if (!status?.lastSeen) return 'unknown'
  
  const timeDiff = Date.now() - status.lastSeen.toMillis()
  return timeDiff <= DEVICE_STATUS_CONSTANTS.ONLINE_THRESHOLD_MS ? 'online' : 'offline'
}

/**
 * Hook for device status calculation with periodic updates
 * Provides real-time connection status and formatted time differences
 * 
 * @param {Object} deviceStatus - Device status object from useDeviceStatus hook
 * @returns {Object} Status calculation results
 */
export const useDeviceStatusCalculation = (deviceStatus) => {
  // Force periodic re-calculation of connection status
  const [updateTrigger, setUpdateTrigger] = useState(0)
  
  useEffect(() => {
    const interval = setInterval(() => {
      setUpdateTrigger(prev => prev + 1)
    }, DEVICE_STATUS_CONSTANTS.STATUS_UPDATE_INTERVAL_MS)
    
    return () => clearInterval(interval)
  }, [])
  
  // Memoize computed values to prevent re-renders
  // Include updateTrigger to force recalculation of time-based status
  const statusCalculation = useMemo(() => {
    if (!deviceStatus?.lastSeen) {
      return {
        connectionStatus: 'unknown',
        connectionConfig: CONNECTION_STATUS_CONFIGS.unknown,
        timeDifferenceMs: null,
        lastSeenText: null,
        isOnline: false,
      }
    }
    
    const timeDifferenceMs = Date.now() - deviceStatus.lastSeen.toMillis()
    const isOnline = timeDifferenceMs <= DEVICE_STATUS_CONSTANTS.ONLINE_THRESHOLD_MS
    const connectionStatus = isOnline ? 'online' : 'offline'
    
    return {
      connectionStatus,
      connectionConfig: CONNECTION_STATUS_CONFIGS[connectionStatus],
      timeDifferenceMs,
      lastSeenText: !isOnline ? formatTimeDifference(timeDifferenceMs) : null,
      isOnline,
    }
    // updateTrigger is intentionally included to force periodic recalculation
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceStatus?.lastSeen, updateTrigger])
  
  return statusCalculation
}

export default useDeviceStatusCalculation
